import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { feedPageQueryOptions } from "../queries/deals";
import { Feed } from "../components/feed";
import { FeedControls, type FeedControlValue } from "../components/feed-controls";

const HomeSearch = z.object({
  q: z.string().optional(),
  category: z.string().optional(),
  merchant: z.string().optional(),
  sort: z.enum(["hot", "new"]).optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute("/")({
  validateSearch: HomeSearch,
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ context, deps }) => {
    const s = deps.search;
    void context.queryClient.prefetchQuery(
      feedPageQueryOptions({
        sort: s.sort ?? "hot",
        category: s.category,
        merchant: s.merchant,
        q: s.q,
        page: s.page ?? 1,
      }),
    );
  },
  component: HomePage,
});

function HomePage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const page = search.page ?? 1;
  const sort = search.sort ?? "hot";

  function apply(next: FeedControlValue) {
    navigate({
      to: "/",
      search: {
        q: next.q,
        category: next.category,
        merchant: next.merchant,
        sort: next.sort === "new" ? "new" : undefined,
        page: undefined,
      },
    });
  }

  return (
    <main className="mx-auto w-full max-w-3xl px-4 sm:px-5 py-6 sm:py-8 flex-1">
      <header className="flex items-baseline justify-between mb-4">
        <h1 className="text-xl font-semibold tracking-tight">{sort === "new" ? "New deals" : "Hot deals"}</h1>
        <nav className="text-sm flex items-center gap-3 text-[color:var(--color-fg-muted)]">
          <Link to="/new" className="hover:text-[color:var(--color-fg)]" activeProps={{ className: "text-[color:var(--color-fg)]" }}>
            New
          </Link>
          <Link
            to="/submit"
            className="rounded-md border border-[color:var(--color-line)] px-3 py-1.5 hover:bg-[color:var(--color-panel)] text-[color:var(--color-fg)]"
          >
            Submit
          </Link>
        </nav>
      </header>
      <FeedControls
        value={{ q: search.q, category: search.category, merchant: search.merchant, sort }}
        onApply={apply}
      />
      <Feed
        sort={sort}
        category={search.category}
        merchant={search.merchant}
        q={search.q}
        page={page}
        onPage={(p) => navigate({ to: "/", search: { ...search, page: p <= 1 ? undefined : p } })}
      />
    </main>
  );
}
